// =============================================================================
// BucketFilterChips: the bucket filter row for the Discover/Jobs tab (brief
// (a): "Bucket filter chips + sort"). A pure, controlled chip row over
// jobBuckets.ts's BUCKET_CHIPS config — renders exactly the buckets present
// in the current job list PLUS 'All', never a bucket the list doesn't
// actually contain (a bucket with zero jobs today still gets a chip only via
// the counts map the caller derives from groupJobsByBucket, so an empty
// bucket simply shows a 0 count rather than being hidden — "kept, never
// deleted, never hidden" extends to the filter UI itself).
// =============================================================================

import { BUCKET_CHIPS, type UNDETERMINED_BUCKET_KEY, type JobBucket } from '../lib/jobBuckets';
import styles from './BucketFilterChips.module.css';
import clsx from './clsx';

export interface BucketFilterChipsProps {
  active: JobBucket | typeof UNDETERMINED_BUCKET_KEY | 'all';
  onChange: (bucket: JobBucket | typeof UNDETERMINED_BUCKET_KEY | 'all') => void;
  /** Count per bucket key, from the currently-loaded job list (jobBuckets.ts's
   *  groupJobsByBucket, sized per group) — the ONLY source of any number this
   *  component renders. A bucket absent from this map shows no count badge
   *  (never a fabricated 0 for a bucket key the caller never computed). */
  counts: Partial<Record<JobBucket | typeof UNDETERMINED_BUCKET_KEY, number>>;
  totalCount: number;
  /** D19b — CRITERIA TRANSPARENCY ("What are we sorting on? I don't know the
   *  sort criteria"). GET /jobs's OWN served declaredInputs.sortCriteria
   *  string (e.g. "home base (not set) · fit · recency") — never
   *  client-derived. Optional + additive: omitted entirely (e.g. during an
   *  active search, which has no server-side sort mode) renders nothing. */
  sortCriteria?: string;
}

export function BucketFilterChips({ active, onChange, counts, totalCount, sortCriteria }: BucketFilterChipsProps) {
  return (
    <div>
      <div className={styles.row} role="group" aria-label="Filter by bucket">
        <button
          type="button"
          className={clsx(styles.chip, active === 'all' && styles.active)}
          onClick={() => onChange('all')}
        >
          All
          <span className={styles.count}>{totalCount}</span>
        </button>
        {BUCKET_CHIPS.map((chip) => {
          const count = counts[chip.key];
          if (!count) return null;
          return (
            <button
              key={chip.key}
              type="button"
              className={clsx(styles.chip, active === chip.key && styles.active)}
              onClick={() => onChange(chip.key)}
              title={chip.note}
            >
              {chip.label}
              <span className={styles.count}>{count}</span>
            </button>
          );
        })}
      </div>
      {sortCriteria ? <div className={styles.sortCriteria}>sorted on: {sortCriteria}</div> : null}
    </div>
  );
}
